import type { ArtifactIndexView, ReceiptView } from "@exploitlens/ui-contracts";

import { shortHex } from "../../lib/format";
import { GateStrip } from "../case/GateStrip";
import { EmptyState } from "../shell/EmptyState";

function exportReceipt(receipt: ReceiptView): void {
  const blob = new Blob([JSON.stringify(receipt, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${receipt.caseId}-receipt.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function EvidenceHashes({ index }: { index: ArtifactIndexView | undefined }) {
  if (!index) {
    return (
      <EmptyState title="Evidence hashes are not available (artifact-index.json was not shipped)." />
    );
  }
  return (
    <div className="overflow-x-auto rounded border border-rule">
      <table className="w-full min-w-[32rem] text-left text-sm">
        <thead>
          <tr className="border-b border-rule bg-canvas-raised text-ink-faint">
            <th scope="col" className="px-3 py-2 font-medium">
              File
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              SHA-256
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Bytes
            </th>
          </tr>
        </thead>
        <tbody>
          {index.files.map((file) => (
            <tr key={file.path} className="border-b border-rule last:border-0">
              <td className="px-3 py-2 font-evidence text-xs text-ink">{file.path}</td>
              <td className="px-3 py-2 font-evidence text-xs text-ink-muted" title={file.sha256}>
                {shortHex(file.sha256)}
              </td>
              <td className="px-3 py-2 text-ink-muted">{file.bytes.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ReceiptPanel({
  receipt,
  artifactIndex,
}: {
  receipt: ReceiptView;
  artifactIndex: ArtifactIndexView | undefined;
}) {
  return (
    <div className="space-y-8">
      <section aria-labelledby="scope-heading">
        <h2 id="scope-heading" className="font-editorial text-xl text-ink">
          Scope of this claim
        </h2>
        {/* Rendered verbatim -- never paraphrased into something more confident (EL-064). */}
        <p className="mt-3 text-ink">{receipt.scope.claim}</p>
        <ul className="mt-3 list-inside list-disc space-y-1.5 text-sm text-ink-muted">
          {receipt.scope.limitations.map((limitation) => (
            <li key={limitation}>{limitation}</li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="receipt-gates-heading">
        <h2 id="receipt-gates-heading" className="font-editorial text-xl text-ink">
          Gates
        </h2>
        <div className="mt-3">
          <GateStrip gates={receipt.gates} />
        </div>
      </section>

      <section aria-labelledby="evidence-hashes-heading">
        <div className="flex items-center justify-between gap-3">
          <h2 id="evidence-hashes-heading" className="font-editorial text-xl text-ink">
            Evidence hashes
          </h2>
          <button
            type="button"
            onClick={() => exportReceipt(receipt)}
            className="rounded border border-rule-strong px-3 py-1.5 text-sm text-ink hover:bg-surface"
          >
            Export receipt (JSON)
          </button>
        </div>
        <div className="mt-3">
          <EvidenceHashes index={artifactIndex} />
        </div>
      </section>
    </div>
  );
}
